import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    static targets = ["element", "select"];

    connect() {
        this.onToggleSet = (e) => this.handleToggleSet(e);
        this.element.addEventListener("toggle:set", this.onToggleSet);

        // Au chargement uniquement : passage synchrone immédiat (immediate:true)
        // pour garantir le déterminisme avant le premier envoi api-sync (cf. #272).
        this.refresh({ immediate: true });

        for (let select of this.selectTargets) {
            const label_ancestor = select.parentElement;
            if (label_ancestor.localName === 'label') {
                label_ancestor.addEventListener('click', (event) => {
                    const changeEvent = new Event('change');
                    select.dispatchEvent(changeEvent);
                    const inputEvent = new Event('input');
                    select.form.dispatchEvent(inputEvent);
                });
            }
        }
    }

    disconnect() {
        try { this.element.removeEventListener("toggle:set", this.onToggleSet); } catch (_) {}
    }

    refresh({ immediate = false } = {}) {
        // Passage synchrone immédiat (uniquement au connect via immediate:true) :
        // on désactive les champs des blocs inactifs avant le premier envoi
        // api-sync (debounce >= 300ms). Les champs disabled étant exclus de
        // FormData, cela garantit qu'un seul <select name="criterias[tax_program]">
        // est sérialisé (le bloc actif) au lieu des deux. Sans ça, la
        // désactivation n'arrivait que dans un setTimeout(100) et pouvait, selon
        // la charge du thread au chargement, se produire APRÈS la construction du
        // payload : les deux selects tax_program (investissement + PTZ/résidence)
        // étaient alors actifs et le "last-wins" choisissait la mauvaise
        // fiscalité (cf. #272).
        //
        // On ne fait PAS ce passage synchrone sur les changements à l'exécution
        // (changed) pour ne pas modifier le timing des bascules de fiscalité du
        // comparateur, dont dépend la rétention des champs force-submit (PTZ).
        if (immediate) {
            this.applyActiveToggles();
        }
        // Pass différé : re-synchronise après le premier layout et l'éventuelle
        // connexion tardive de cibles (comportement historique).
        setTimeout(() => this.applyActiveToggles(), 100);
    }

    applyActiveToggles() {
        for (let select of this.selectTargets) {
            if (select.disabled === false && (select.type != 'radio' || (select.type == 'radio' && select.checked))) {
                this.toggleElements(select.value, select.dataset.toggleScope);
            }
        }
        this.hideEmptyProfileBlocks();
    }

    handleToggleSet(event) {
        try {
            const detail = event && event.detail ? event.detail : null;
            const scope = detail && detail.scope ? String(detail.scope) : null;
            const value = detail && detail.value ? String(detail.value) : null;
            if (!scope || !value) return;
            this.toggleElements(value, scope);
            // `toggle:set` n'appelle pas `applyActiveToggles` : le bloc vide
            // doit quand même se mettre à jour après cette bascule.
            this.hideEmptyProfileBlocks();
        } catch (_) {
            // no-op
        }
    }

    changed(event) {
        this.refresh();
    }

    toggleElements(value, scope) {
        if (this.hasElementTarget) {
            for (let element of this.elementTargets) {
                if (element.dataset.toggleScope == scope) {
                    this.toggle(element, element.dataset.values, element.dataset.valuesHide, value);
                }
            }
        }
    }

    toggle(element, values, valuesHide, value) {
        let hidden = false;

        // Check if the element should be visible
        if (values) {
            hidden = !values.split(",").includes(value);
        }

        // Check if the element should be hidden due to valuesHide
        if (valuesHide && valuesHide.split(",").includes(value)) {
            hidden = true;
        }

        let originalVisibilityState = element.hidden;
        element.hidden = hidden;

        // Un bloc masqué désactive ses champs : ils sortent du POST (FormData
        // ignore `disabled`). Exception opt-in (#727), portée par
        // `data-toggle-keep-enabled="true"` sur CE bloc seulement : les taux de
        // revalorisation restent sérialisés en résidence principale. Les autres
        // blocs masqués continuent de désactiver leurs champs.
        const keepEnabled = element.dataset.toggleKeepEnabled === 'true';

        element.querySelectorAll('input,select').forEach(input => {
            // Ne pas réactiver les champs volontairement désactivés (ex. impôt / TMI calculés)
            const preserveDisabled = input.dataset.togglePreserveDisabled === 'true';
            if (hidden) {
                input.disabled = keepEnabled ? preserveDisabled : true;
            } else {
                input.disabled = preserveDisabled;
            }
        });
    }

    // Un bloc profil qui ne montre plus aucun champ ni encart calculé
    // disparaît entièrement (titre compris). Masquage visuel seulement :
    // pas de `hidden`, pas de désactivation — les radios `tax_program` et les
    // champs seulement `d-none` (config) continuent de partir dans le POST.
    //
    // Un `d-none` déjà posé par le serveur (bloc masqué par la config, #710)
    // est conservé : les champs héritent de ce masquage, le bloc reste vide.
    // Le `d-none` posé ici est marqué `data-simulator-block-empty` pour
    // pouvoir le retirer dès qu'un contenu redevient affiché. Sans cette
    // marque, le `d-none` du bloc lui-même rendrait tous ses descendants
    // « non affichés » et le bloc ne réapparaîtrait jamais.
    //
    // Les blocs sans `[data-simulator-field]` (colonne acquisition) et les
    // widgets d'effort, qui n'ont pas ces attributs, ne sont pas concernés.
    hideEmptyProfileBlocks() {
        const blocks = this.element.querySelectorAll('[data-simulator-block]');
        for (let block of blocks) {
            if (!block.querySelector('[data-simulator-field]')) continue;

            const ownedByToggle = block.dataset.simulatorBlockEmpty === 'true';
            if (block.classList.contains('d-none') && !ownedByToggle) continue;

            const empty = !this.profileBlockShowsContent(block);
            block.classList.toggle('d-none', empty);
            if (empty) {
                block.dataset.simulatorBlockEmpty = 'true';
            } else {
                delete block.dataset.simulatorBlockEmpty;
            }
        }
    }

    // Champ (`[data-simulator-field]`) ou encart calculé. Les encarts portent
    // `data-controller="remote-bind"` (et `data-remote-bind-*`), pas un
    // attribut `data-remote-bind` nu : les deux sélecteurs sont acceptés.
    profileBlockShowsContent(block) {
        const nodes = block.querySelectorAll(
            '[data-simulator-field], [data-remote-bind], [data-controller~="remote-bind"]'
        );
        for (let node of nodes) {
            if (this.effectivelyShown(node, block)) return true;
        }
        return false;
    }

    // Affiché : ni `d-none` ni `hidden` sur le nœud, ni sur un ancêtre
    // jusqu'au bloc inclus. Le `d-none` que ce contrôleur a lui-même posé
    // sur le bloc est ignoré, sinon un contenu redevenu visible ne pourrait
    // plus rouvrir le bloc.
    effectivelyShown(node, block) {
        let current = node;
        while (current) {
            const isBlock = current === block;
            const ignoreBlockDnone = isBlock && block.dataset.simulatorBlockEmpty === 'true';
            if (!ignoreBlockDnone && current.classList.contains('d-none')) return false;
            if (current.hasAttribute('hidden')) return false;
            if (isBlock) return true;
            current = current.parentElement;
        }
        return false;
    }
}